import React, { useState } from "react";
import { Box, HStack } from "@chakra-ui/layout";
import {
  Button,
  SkeletonText,
  Tag,
  TagLabel,
  TagRightIcon,
  Text,
  Link as ChakraLink,
} from "@chakra-ui/react";
import { PageContent } from "../../components/PageContent/PageContent";
import { PageHeading } from "../../components/PageHeading/PageHeading";
import { ErrorMessage } from "../../components/ErrorMessage/ErrorMessage";
import { LoadingIndicator } from "../../components/LoadingIndicator/LoadingIndicator";
import {
  FilterIcon,
  ClearFilterIcon,
  CloseCircleIcon,
} from "../../styles/icons";
import { usePaginatedAdminApiRequest } from "../../hooks/usePaginatedApiRequest";
import { CherryPayCardsTable } from "../../components/CherryPayCards/CherryPayCardsTable";
import {
  CherryPayCardFilterModal,
  CherryPayCardFilterType,
} from "../../components/CherryPayCards/CherryPayCardFilterModal";
import { useSearchInput } from "../../hooks/useSearchInput";
import { useDisclosure } from "@chakra-ui/react";
import { cloneDeep } from "lodash";

export const CherryPayCards = () => {
  const [cardFilter, setCardFilter] = useState<CherryPayCardFilterType>();
  const filterModal = useDisclosure();

  const { debouncedQuery, element: searchInputElement } = useSearchInput({
    placeholder: "Search by External Account ID or card...",
  });

  const onSuccessFilter = (filter?: CherryPayCardFilterType) => {
    setCardFilter(filter);
    filterModal.onClose();
  };

  const {
    items: cards,
    refresh,
    isLoading,
    isLoadingPage,
    error,
    fetchNextPage,
  } = usePaginatedAdminApiRequest(
    (apiClient) =>
      apiClient.getCherryPayCards({
        q: debouncedQuery || undefined,
        businessId: cardFilter?.businessId,
        externalAccountId: cardFilter?.externalAccountId,
      }),
    (apiClient, _, continuationToken) =>
      apiClient.getCherryPayCards({
        q: debouncedQuery || undefined,
        businessId: cardFilter?.businessId,
        externalAccountId: cardFilter?.externalAccountId,
        continuationToken,
      }),
    [debouncedQuery, cardFilter]
  );

  const closeFilter = (key: keyof CherryPayCardFilterType) => {
    const uFilter = cloneDeep(cardFilter) as Record<string, string | undefined>;
    delete uFilter[key];
    setCardFilter(
      Object.keys(uFilter).length
        ? (uFilter as CherryPayCardFilterType)
        : undefined
    );
  };

  const clearFilter = () => {
    setCardFilter(undefined);
  };

  return (
    <>
      <PageHeading>
        <PageHeading.Title>CherryPay Cards</PageHeading.Title>
      </PageHeading>
      <PageContent>
        <HStack w="100%">
          <Box flex="1" maxWidth="400px">
            {searchInputElement}
          </Box>
          <Box flexGrow={1}>
            <Button
              onClick={filterModal.onOpen}
              alignSelf="flex-end"
              colorScheme="cherryButton"
              leftIcon={<FilterIcon />}
            >
              Filters
            </Button>
          </Box>
        </HStack>
        <HStack w="100%" alignItems="flex-start" color="gray.600">
          {(cardFilter && Object.keys(cardFilter).length > 0 && (
            <>
              <Text>
                Filter |{" "}
                <ChakraLink onClick={clearFilter}>
                  <ClearFilterIcon /> Clear{" "}
                </ChakraLink>{" "}
                :{" "}
              </Text>
              {Object.entries(cardFilter).map(
                ([key, value]) =>
                  value && (
                    <Tag
                      size="sm"
                      key={key}
                      cursor="pointer"
                      colorScheme="cherryButton"
                      color="white"
                      onClick={() =>
                        closeFilter(key as keyof CherryPayCardFilterType)
                      }
                    >
                      <TagLabel>
                        {key}: {value}
                      </TagLabel>
                      <TagRightIcon as={CloseCircleIcon} />
                    </Tag>
                  )
              )}
            </>
          )) || (
            <>
              <Text>Filter :</Text>
              <Text fontStyle="italic">No Filter Applied</Text>
            </>
          )}
        </HStack>

        <CherryPayCardsTable
          cards={cards}
          isLoading={isLoading}
          debouncedQuery={debouncedQuery}
        />
        {fetchNextPage && !isLoading && (
          <Box w="100%">
            {!isLoadingPage && (
              <Button colorScheme="cherryButton" onClick={fetchNextPage}>
                Load more...
              </Button>
            )}
            {isLoadingPage && (
              <HStack width="100%" boxShadow="lg">
                <Box width="20%" padding="4" pr="0">
                  <SkeletonText mt="4" noOfLines={4} spacing="4" />
                </Box>
                <Box width="30%" padding="4" pr="0">
                  <SkeletonText mt="4" noOfLines={4} spacing="4" />
                </Box>
                <Box width="40%" padding="4" pr="0">
                  <SkeletonText mt="4" noOfLines={4} spacing="4" />
                </Box>
                <Box width="5%" pt="4" pb="4">
                  <SkeletonText mt="4" noOfLines={4} spacing="4" />
                </Box>
                <Box width="5%" pt="4" pb="4" pr="4">
                  <SkeletonText mt="4" noOfLines={4} spacing="4" />
                </Box>
              </HStack>
            )}
          </Box>
        )}
        {error != null && (
          <ErrorMessage>
            {error ?? "An error was encountered while loading CherryPay cards."}
          </ErrorMessage>
        )}
        {(!cards || isLoading) && <LoadingIndicator />}
        <CherryPayCardFilterModal
          {...filterModal}
          filter={cardFilter}
          onSuccess={onSuccessFilter}
        />
      </PageContent>
    </>
  );
};
